// frontend/src/pages/dashboard/ProjectComponents/ProjectTableRowSkeleton.js
import React from 'react';
import { TableRow, TableCell, Box, Skeleton } from '@mui/material';

const ProjectTableRowSkeleton = ({ isAdmin = false, isMobile = false, isSmallDesktop = false }) => {
  // Match the column visibility logic from ProjectTable
  const columnVisibility = {
    index: true,
    name: true,
    startDate: !isSmallDesktop,
    endDate: !isSmallDesktop,
    estimatedHours: isAdmin && !isMobile,
    status: true,
    sprint: !isSmallDesktop,
    assigned: !isSmallDesktop,
    client: !isMobile,
    billing: isAdmin && !isSmallDesktop,
    attachments: true,
    actions: true,
  };

  const columnWidths = {
    index: { xs: 40, sm: 50, md: 60 },
    name: { min: { xs: 180, md: 220 }, max: 280 },
    startDate: { min: 100, max: 120, width: 110 },
    endDate: { min: 100, max: 120, width: 110 },
    estimatedHours: { min: 85, max: 100, width: 90 },
    status: { min: 85, max: 110, width: 100 },
    sprint: { min: 90, max: 120, width: 100 },
    assigned: { min: 100, max: 140, width: 120 },
    client: { min: 100, max: 140, width: 120 },
    billing: { min: 120, max: 160, width: 140 },
    attachments: { min: 60, max: 80, width: 70 },
    actions: { min: isSmallDesktop ? 90 : 150, max: isSmallDesktop ? 90 : 170, width: isSmallDesktop ? 90 : 150 },
  };

  const headerCellStyle = {
    py: 1.5,
    px: 1,
    boxSizing: 'border-box',
  };

  // Render multiple skeleton rows (5 rows to match typical loading state)
  return (
    <>
      {[...Array(5)].map((_, rowIndex) => (
        <TableRow key={rowIndex} hover={false}>
          {columnVisibility.index && (
            <TableCell align="center" sx={{ ...headerCellStyle, width: columnWidths.index.md, minWidth: columnWidths.index.md, maxWidth: columnWidths.index.md }}>
              <Skeleton variant="text" width={30} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.name && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.name.min.md, maxWidth: columnWidths.name.max }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Skeleton variant="text" width="80%" height={24} />
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            </TableCell>
          )}
          
          {columnVisibility.startDate && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.startDate.min, maxWidth: columnWidths.startDate.max, width: columnWidths.startDate.width }}>
              <Skeleton variant="text" width={80} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.endDate && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.endDate.min, maxWidth: columnWidths.endDate.max, width: columnWidths.endDate.width }}>
              <Skeleton variant="text" width={80} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.estimatedHours && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.estimatedHours.min, maxWidth: columnWidths.estimatedHours.max, width: columnWidths.estimatedHours.width }}>
              <Skeleton variant="text" width={60} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.status && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.status.min, maxWidth: columnWidths.status.max, width: columnWidths.status.width }}>
              <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} />
            </TableCell>
          )}
          
          {columnVisibility.sprint && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.sprint.min, maxWidth: columnWidths.sprint.max, width: columnWidths.sprint.width }}>
              <Skeleton variant="rectangular" width={80} height={22} sx={{ borderRadius: 1 }} />
            </TableCell>
          )}
          
          {columnVisibility.assigned && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.assigned.min, maxWidth: columnWidths.assigned.max, width: columnWidths.assigned.width }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {[1, 2, 3].map((item) => (
                  <Skeleton key={item} variant="circular" width={24} height={24} />
                ))}
              </Box>
            </TableCell>
          )}
          
          {columnVisibility.client && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.client.min, maxWidth: columnWidths.client.max, width: columnWidths.client.width }}>
              <Skeleton variant="text" width={90} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.billing && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.billing.min, maxWidth: columnWidths.billing.max, width: columnWidths.billing.width }}>
              <Skeleton variant="text" width={100} height={20} />
            </TableCell>
          )}
          
          {columnVisibility.attachments && (
            <TableCell sx={{ ...headerCellStyle, minWidth: columnWidths.attachments.min, maxWidth: columnWidths.attachments.max, width: columnWidths.attachments.width }}>
              <Skeleton variant="circular" width={24} height={24} />
            </TableCell>
          )}
          
          {columnVisibility.actions && (
            <TableCell align="right" sx={{ ...headerCellStyle, minWidth: columnWidths.actions.min, maxWidth: columnWidths.actions.max, width: columnWidths.actions.width }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                {[1, 2, 3, 4, 5].map((item) => (
                  <Skeleton key={item} variant="circular" width={28} height={28} />
                ))}
              </Box>
            </TableCell>
          )}
        </TableRow>
      ))}
    </>
  );
};

export default ProjectTableRowSkeleton;